import { gql, useLazyQuery } from "@apollo/client";
import { useEffect, useState, useContext } from "react";
import { convertToCSV } from "@solsys/solsys-common";
import {
  OptionsMenu,
  Button,
  NotificationContext,
  ResponsiveTableView,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { FaPlus, FaCog, FaFileCsv } from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import { isTechnician, isCustomer } from "../../utils/roles";
import { useUserList } from "../../contexts/UserListContext";
import CraneDropDown from "../../dropdowns/CraneDropDown";
import { withTimestampSortOptions, resetListSort } from "../../listSort";
import useApolloListData from "../../useApolloListData";

const gqlQuery = gql`
  query ticketList($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      edges {
        id
        name
        status_id
        ticket_status {
          id
          name
        }
        created_at
        crane {
          id
          name
        }
        ticket2Technician {
          edges {
            user_id
          }
        }
      }
      totalCount
    }
  }
`;

export const ListOptions = {
  sortBy: "sortBy",
  sortDirection: "sortDirection",
  name: "name",
  statusId: "status",
  subscribedOnly: "subscribed_only",
  search: "search",
};

export default function TicketList({ id: entityId, action, options = {}, ...props }) {
  const { addNotification } = useContext(NotificationContext);
  const { user } = useAuth();
  const userList = useUserList();
  const isAdd = action === "add";

  const [requestState, setRequestState] = props.state;
  let { sortBy, sortDirection, name, statusId, craneId, subscribedOnly, search } = requestState;

  const isTech = isTechnician(user);
  const isCust = isCustomer(user);
  const underCrane = Boolean(options.crane_id);
  const defaultStatusId = (underCrane && !isCust) || isTech ? "1" : null;
  // Standalone ticket MDV: techs default to assigned-only + open status.
  // Under crane / tech status defaults are also set in TicketController.
  const defaultPatch = {};
  if (subscribedOnly === undefined && isTech && !underCrane) {
    subscribedOnly = true;
    defaultPatch.subscribedOnly = true;
  }
  if (statusId === undefined && defaultStatusId) {
    statusId = defaultStatusId;
    defaultPatch.statusId = defaultStatusId;
  }
  if (Object.keys(defaultPatch).length) {
    setRequestState({ ...requestState, ...defaultPatch });
  }

  const setListOptions = (state) =>
    setRequestState({ ...requestState, ...state });
  const [optionSortBy, setOptionSortBy] = useState(sortBy);
  const [optionSortDirection, setOptionSortDirection] = useState(sortDirection);
  const [optionName, setOptionName] = useState(name);
  const [optionStatusId, setOptionStatusId] = useState(
    statusId !== undefined && statusId !== null ? statusId : defaultStatusId
  );
  const [optionCraneId, setOptionCraneId] = useState(craneId);
  const [optionSubscribedOnly, setOptionSubscribedOnly] = useState(
    subscribedOnly !== undefined ? subscribedOnly : (underCrane ? false : isTech)
  );
  const applyOptions = () => {
    setListOptions({
      sortBy: optionSortBy,
      sortDirection: optionSortDirection,
      name: optionName,
      statusId: optionStatusId,
      craneId: optionCraneId,
      subscribedOnly: optionSubscribedOnly,
    });
  };
  const resetOptions = () => {
    const defaults = resetListSort();
    setOptionSortDirection(defaults.sortDirection);
    setOptionSortBy(defaults.sortBy);
    setOptionName("");
    setOptionStatusId(defaultStatusId);
    setOptionCraneId(null);
    setOptionSubscribedOnly(underCrane ? false : isTech);
  };

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [limit, setLimit] = useState(100);

  const sortByOptions = withTimestampSortOptions([
    { text: "Ticket ID", sortBy: "id" },
    { text: "Name", sortBy: "name" },
  ]);
  const sortDirectionOptions = [
    {
      text: "A to Z",
      sortDirection: "ASC",
    },
    {
      text: "Z to A",
      sortDirection: "DESC",
    },
  ];

  let variables = {
    ticket_list_request: {
      filters: [],
      pagination: { limit },
      sorting: [],
    },
  };

  // Add crane_id filter if provided in options
  if (options.crane_id) {
    variables.ticket_list_request.filters.push({
      field: "crane_id",
      operator: "=",
      value: String(options.crane_id),
    });
  }

  variables.ticket_list_request.sorting.push({
    field: "status_id",
    direction: "ASC",
  });

  if (sortBy && sortDirection) {
    let sortOption = sortByOptions.find((v) => v.sortBy === sortBy);
    variables.ticket_list_request.sorting.push({
      field: sortBy,
      direction: sortDirection,
      entity: sortOption?.sortEntity,
    });
  }
  if (name)
    variables.ticket_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${name}%`,
    });
  if (search?.trim())
    variables.ticket_list_request.filters.push({
      field: "search",
      value: search.trim(),
    });
  if (craneId)
    variables.ticket_list_request.filters.push({
      field: "crane_id",
      operator: "=",
      value: String(craneId),
    });
  if (statusId)
    variables.ticket_list_request.filters.push({
      field: "status_id",
      operator: "=",
      value: String(statusId),
    });
  if (subscribedOnly && user?._id)
    variables.ticket_list_request.filters.push({
      field: "subscription_user_id",
      operator: "=",
      value: String(user._id),
    });

  const [csvQuery, { loading: csvLoading }] = useLazyQuery(gqlQuery);
  const [refetch, { loading, data }] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 5000,
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });
  const list = useApolloListData(data, (localData) => localData?.ticket?.edges);
  useEffect(() => {
    if (props.onStateChange) props.onStateChange(requestState);
    refetch({ variables });
  }, [requestState, limit]);
  const count = data?.ticket?.totalCount ?? 0;
  if (props.isMaster && props.autoSelect && !loading && !isAdd && !entityId) {
    // props.onSelect(0);
    props.onSelect(list.length ? list[0].id : 0);
  }

  const formatCreatedAt = (iso) => {
    if (!iso) return "—";
    const dt = DateTime.fromISO(iso, { zone: "utc" });
    return dt.isValid ? dt.toLocal().toFormat("MMM d, yyyy") : "—";
  };

  return (
    <OptionsMenu isOpen={ isFilterMenuOpen }>
      <OptionsMenu.Menu>
        <div className="options-tools">
          <Button
            onClick={ () => {
              applyOptions();
              setIsFilterMenuOpen(false);
            } }
            loading={ loading }
          >
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>

        <div className="options-section">
          <h3>Filters</h3>

          <div className="option">
            <label>Ticket Name:</label>
            <input
              value={ optionName }
              onChange={ (e) => setOptionName(e.target.value) }
            />
          </div>

          <div className="option">
            <label>Status:</label>
            <select
              value={ optionStatusId ?? "" }
              onChange={ (e) => setOptionStatusId(e.target.value) }
            >
              <option value="">All</option>
              <option value="1">Open</option>
              <option value="2">Closed</option>
            </select>
          </div>

          { !isCust && (
            <div className="option">
              <label>
                <input
                  type="checkbox"
                  checked={ optionSubscribedOnly }
                  onChange={ (e) => setOptionSubscribedOnly(e.target.checked) }
                  style={ { marginRight: "8px" } }
                />
                Assigned Tickets Only
              </label>
            </div>
          ) }

          { !options.crane_id && (
            <div className="option">
              <label>Crane:</label>
              <CraneDropDown
                selected={ optionCraneId }
                onChange={ (selectedCrane) => setOptionCraneId(selectedCrane) }
                searchEnabled={ true }
                multiple={ false }
                disabled={ false }
              />
            </div>
          ) }
        </div>

        <div className="options-section">
          <h3>Sorting</h3>

          <div className="option">
            <label>Sort By:</label>
            <div>
              <select
                value={ optionSortBy }
                onChange={ (event) => setOptionSortBy(event.target.value) }
              >
                { sortByOptions.map((option) => (
                  <option key={ option.text } value={ option.sortBy }>
                    { option.text }
                  </option>
                )) }
              </select>
            </div>
          </div>

          <div className="option">
            <label>Sort Order:</label>
            <div>
              <select
                value={ optionSortDirection }
                onChange={ (event) => setOptionSortDirection(event.target.value) }
              >
                { sortDirectionOptions.map((option) => (
                  <option key={ option.text } value={ option.sortDirection }>
                    { option.text }
                  </option>
                )) }
              </select>
            </div>
          </div>
        </div>

        <div className="options-tools">
          <Button
            onClick={ () => {
              applyOptions();
              setIsFilterMenuOpen(false);
            } }
            loading={ loading }
          >
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>
      </OptionsMenu.Menu>
      <OptionsMenu.Layout>
        <div className="page-header">
          <span className="page-header-title">Tickets</span>
          <div className="page-header-options">
            <Button
              onClick={ () => {
                variables.ticket_list_request.pagination = { limit: 10000 };
                csvQuery({ variables }).then((response) => {
                  const csvData = convertToCSV(
                    response?.data?.ticket?.edges.map((entity) => ({
                      ID: entity.id,
                      Name: entity.name,
                      Crane: entity.crane?.name || "",
                      Status: entity.ticket_status?.name || (entity.status_id === 1 ? "Open" : "Closed"),
                      Created: formatCreatedAt(entity.created_at),
                    }))
                  );
                  const blob = new Blob([csvData], {
                    type: "text/csv;charset=utf-8;",
                  });
                  const url = URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute(
                    "download",
                    `tickets_${DateTime.now()
                      .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
                      .replace(/:/g, "-")
                      .replace(/, /g, "_")}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                });
              } }
              loading={ csvLoading }
            >
              <FaFileCsv />
            </Button>
            { props.isMaster && (
              <button onClick={ () => props.onSelect(0) }>
                <FaPlus />
              </button>
            ) }
            <button onClick={ () => setIsFilterMenuOpen(true) }>
              <FaCog />
            </button>
          </div>
        </div>

        <input
          value={ search ?? "" }
          style={ { width: "100%" } }
          placeholder="Search ID, name, or description..."
          type="text"
          onChange={ (e) => {
            setListOptions({ search: e.target.value });
          } }
        />
        <ResponsiveTableView
          style={ { height: "100%" } }
          loading={ loading }
          headers={ [
            {
              label: "Name",
            },
            {
              label: "Crane",
            },
            {
              label: "Technician",
            },
            {
              label: "Status",
            },
            {
              label: "Created",
            },
          ] }
          data={ list.map((entity) => {
            const techUserIds = (entity.ticket2Technician?.edges ?? [])
              .map(e => e.user_id)
              .filter(Boolean);
            const techNames = techUserIds
              .map(uid => {
                const u = userList.find(u => u._id === uid);
                return u?.name || u?.username || null;
              })
              .filter(Boolean);
            return {
              id: entity.id,
              Name: (
                <div className="ticket-name-container">
                  <div className={ `ticket-status-indicator ${entity.status_id === 1 ? 'open' : 'closed'}` } />
                  <span>{ entity.name }</span>
                </div>
              ),
              Crane: entity.crane?.name || "—",
              Technician: techNames.length ? techNames.join(", ") : "—",
              Status: entity.ticket_status?.name || (entity.status_id === 1 ? "Open" : "Closed"),
              Created: formatCreatedAt(entity.created_at),
              selected: String(entity.id) === String(entityId) && !isAdd,
            };
          }) }
          identityColumnKey="Name"
          onRowClick={ (row) => {
            props.onSelect(row.id, true);
          } }
          totalCount={ count }
          addMoreResults={ () => {
            setLimit(limit + 100);
          } }
        />
      </OptionsMenu.Layout>
    </OptionsMenu>
  );
}
